/**
 * BlueskyConnectModal — verbindet Bluesky per Handle + App-Passwort.
 *
 * Bluesky hat keinen einfachen OAuth-Redirect; der stabile, offizielle Weg ist ein
 * App-Passwort (Bluesky → Einstellungen → App-Passwörter). Dieses Modal sammelt nur
 * Handle + App-Passwort und ruft den Backend-Connect auf — kein Klartext-Speichern im
 * Frontend, keine vorgetäuschte Verbindung.
 */
import { useState } from "react";
import { X, Loader2, KeyRound, ExternalLink, UserPlus } from "lucide-react";
import { connectBluesky } from "../../services/socialOAuth";

/**
 * Normalisiert die Handle-Eingabe: führendes @ entfernen, und wenn der Nutzer nur
 * seinen Namen ohne Domain eingibt (naheliegend), „.bsky.social" ergänzen. Enthält
 * die Eingabe bereits einen Punkt (Custom-Domain) oder ein @ (E-Mail), bleibt sie.
 */
export function normalizeBlueskyHandle(raw) {
  let h = (raw || "").trim().replace(/^@+/, "");
  if (!h) return h;
  if (h.includes("@")) return h;          // E-Mail als Identifier → unverändert
  if (!h.includes(".")) h = `${h}.bsky.social`;
  return h;
}

export default function BlueskyConnectModal({ open, onClose, onConnected }) {
  const [handle, setHandle] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const submit = async () => {
    setError(null);
    const normHandle = normalizeBlueskyHandle(handle);
    if (!normHandle || !appPassword.trim()) {
      setError("Bitte Handle und App-Passwort eingeben.");
      return;
    }
    setBusy(true);
    const res = await connectBluesky(normHandle, appPassword.trim());
    setBusy(false);
    if (res.ok) {
      onConnected?.(res);
      setHandle(""); setAppPassword("");
    } else {
      setError(res.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center">
              <KeyRound size={16} className="text-sky-600" />
            </div>
            <h3 className="font-semibold text-stone-800">Bluesky verbinden</h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-stone-500">
            Bluesky verbindet sich mit deinem <strong>Handle</strong> und einem{" "}
            <strong>App-Passwort</strong> — keine App-Registrierung nötig. In der Testphase
            veröffentlichst du kostenlos über Bluesky.
          </p>

          {/* Noch kein Bluesky-Konto? (Zielgruppe hat es meist nicht) */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-sky-50 border border-sky-100">
            <UserPlus size={15} className="text-sky-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-sky-800 leading-snug">
              Noch kein Bluesky-Konto?{" "}
              <a href="https://bsky.app" target="_blank" rel="noreferrer"
                 className="underline font-medium hover:text-sky-900">
                In 1 Minute kostenlos registrieren
              </a>{" "}– danach hierher zurückkommen.
            </p>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-stone-500">Handle</span>
            <input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="name.bsky.social"
              autoCapitalize="none" autoCorrect="off"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
            <span className="mt-1 block text-xs text-stone-400">
              Nur dein Name reicht — „.bsky.social" ergänzen wir automatisch.
            </span>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-stone-500">App-Passwort</span>
            <input
              type="password"
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
              placeholder="xxxx-xxxx-xxxx-xxxx"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
            {/* Nummerierte Kurzanleitung — das App-Passwort ist NICHT das Login-Passwort */}
            <ol className="mt-2 space-y-1 text-xs text-stone-500 list-decimal list-inside">
              <li>In Bluesky: Einstellungen → „App-Passwörter"</li>
              <li>„App-Passwort hinzufügen" → Namen vergeben (z. B. Förderfuchs)</li>
              <li>Das angezeigte Passwort kopieren (wird nur einmal gezeigt) und hier einfügen</li>
            </ol>
            <a
              href="https://bsky.app/settings/app-passwords"
              target="_blank" rel="noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700"
            >
              App-Passwort in Bluesky erstellen <ExternalLink size={11} />
            </a>
          </label>

          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-stone-100">
          <button onClick={onClose} disabled={busy} className="text-sm text-stone-500 hover:text-stone-700">Abbrechen</button>
          <button
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-sm font-medium disabled:opacity-40"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
            {busy ? "Verbinde…" : "Verbinden"}
          </button>
        </div>
      </div>
    </div>
  );
}
